import * as Yup from 'yup';
import { ENVIRONMENT_KEYS } from 'shared/constants';
import { currentEnvironment } from 'shared/services/Environment';
import { getImageDimensions } from 'shared/utils';

export const WORKSPACE_SCHEMA_NAME = Yup.object().shape({
  name: Yup.string().min(2).required(),
});

export const WORKSPACE_SCHEMA_CALENDAR_URL = Yup.object().shape({
  calendarUrl: Yup.string().url(),
});

export const WORKSPACE_SCHEMA_SCHEDULE_URLS = Yup.object().shape({
  roles: Yup.array().of(Yup.string()).default([]),
  professions: Yup.array().of(Yup.string()).default([]),
  url: Yup.string().url().required(),
  title: Yup.string().min(2).default(''),
});

export const WORKSPACE_SCHEMA_QUICK_LINKS = Yup.object().shape({
  title: Yup.string().min(2).required(),
  url: Yup.string()
    .required()
    .test('is-valid-url', 'url must be a valid URL', value => {
      if (!value) return false;
      // Allow mailto:, http://, and https:// URLs
      if (/^(mailto:|https?:\/\/)/.test(value)) return true;
      return false;
    }),
  iconUrl:
    currentEnvironment() === ENVIRONMENT_KEYS.DEVELOPMENT
      ? Yup.string().not([null, '']).required('Icon URL is required')
      : Yup.string().url().required('Icon URL is required'),
});

export const WORKSPACE_COPY_SCHEMA = Yup.object().shape({
  workspaceId: Yup.string().required('Please select a workspace to copy from'),
  copyCalendarUrl: Yup.bool(),
  copyQuickLinks: Yup.bool(),
  copyScheduleUrls: Yup.bool(),
  copyDashboardBanner: Yup.bool(),
});

export const DASHBOARD_BANNER_MOBILE_DIMENSIONS = {
  width: 600,
  height: 600,
};

export const DASHBOARD_BANNER_WEB_DIMENSIONS = {
  width: 2200,
  height: 180,
};

export const WORKSPACE_SCHEMA_IMAGE_LINK = Yup.object().shape({
  imageLink: Yup.string().nullable().url(),
});

export const createBannerImageSchema = expectedDimensions => {
  const schema = Yup.string()
    .nullable()
    .test(
      'valid-dimension',
      `Image must be ${expectedDimensions.width}x${expectedDimensions.height}`,
      async function (value) {
        if (!value) {
          return true;
        }
        try {
          const { width, height } = await getImageDimensions(value);
          const isValid =
            width === expectedDimensions.width && height === expectedDimensions.height;
          if (!isValid) {
            return this.createError({
              message: `Image must be ${expectedDimensions.width}x${expectedDimensions.height}. Current: ${width}x${height}`,
            });
          }
        } catch (e) {
          return false;
        }
        return true;
      },
    );

  return currentEnvironment() === ENVIRONMENT_KEYS.DEVELOPMENT ? schema : schema.url();
};

const mobileImageSchema = createBannerImageSchema(DASHBOARD_BANNER_MOBILE_DIMENSIONS);
const webImageSchema = createBannerImageSchema(DASHBOARD_BANNER_WEB_DIMENSIONS);

export const WORKSPACE_DASHBOARD_BANNER_SCHEMA = Yup.object().shape({
  mobileImage: mobileImageSchema,
  webImage: webImageSchema,
  imageLink: Yup.string().nullable().url(),
});

export const CREATE_WORKSPACE_SCHEMA = Yup.object().shape({
  name: WORKSPACE_SCHEMA_NAME.fields.name,
  operational: Yup.object().shape({
    calendarUrl: WORKSPACE_SCHEMA_CALENDAR_URL.fields.calendarUrl,
    quickLinks: Yup.array().of(WORKSPACE_SCHEMA_QUICK_LINKS),
    scheduleUrls: Yup.array().of(WORKSPACE_SCHEMA_SCHEDULE_URLS),
    dashboardBanner: WORKSPACE_DASHBOARD_BANNER_SCHEMA,
  }),
});
